// React / Next
import { ReactNode } from 'react';

// Types
type TextPosition = 'left' | 'right';

interface ValuePropositionBlockProps {
  /** Title of the value proposition */
  title: string;
  /** Description text */
  description: string;
  /** The magic vector component to display */
  vector: ReactNode;
  /** Position of the text content - 'left' or 'right' */
  textPosition?: TextPosition;
  /** Additional className for the wrapper */
  className?: string;
}

/**
 * ValuePropositionBlock - A reusable component that displays a value proposition
 * with a title, description, and animated magic vector side by side.
 * Text can be positioned on the left or right side.
 */
export const ValuePropositionBlock = ({
  title,
  description,
  vector,
  textPosition = 'left',
  className = '',
}: ValuePropositionBlockProps) => {
  // Desktop: respect textPosition prop (left = text first, right = vector first)
  // Mobile: vector ALWAYS first (order-1), text ALWAYS second (order-2)
  const textOrderClass =
    textPosition === 'left' ? 'md:order-1 order-2' : 'md:order-2 order-2';
  const vectorOrderClass =
    textPosition === 'left' ? 'md:order-2 order-1' : 'md:order-1 order-1';

  return (
    <div
      className={`flex items-start gap-[24px] w-full max-w-[1016px] max-md:flex-col max-md:items-center ${className}`}
    >
      <div
        className={`flex flex-col gap-[16px] w-[496px] pt-[58px] max-md:w-full max-md:max-w-[496px] max-md:pt-0 ${textOrderClass}`}
      >
        {/* Blue decorative line */}
        <div className="w-[88px] h-[4px] bg-blue-1000 rounded-full" />

        {/* Title */}
        <h2 className="font-heading text-[56px] leading-[64px] tracking-[-1px] font-light text-blue-1000 max-md:text-[40px] max-md:leading-[48px]">
          {title}
        </h2>

        {/* Description */}
        <p className="font-body text-[16px] leading-[24px] text-blue-1000">
          {description}
        </p>
      </div>
      <div
        className={`w-[496px] h-[455px] shrink-0 max-md:w-full max-md:h-auto max-md:aspect-[496/455] ${vectorOrderClass}`}
      >
        {vector}
      </div>
    </div>
  );
};

